import { saveAs } from "file-saver";
import type { MouseEvent } from "react";
import { useState } from "react";

import type { CustomDropdownItemComponentProps } from "common/components/dropdown/Dropdown";
import { DropdownItem } from "common/components/dropdown/DropdownItem";
import { getImageDownloadFile } from "common/utilities";
import { useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { useErrorModal } from "domains/modal/utils";
import { StyledLoadingIndicator } from "domains/topicDetail/components/advancedOptions/LoadingIndicator";
import { getTmTopic } from "domains/transformationMap/selectors";

export const TopicImageExportButton = ({ isActive, index, handleDeactivate }: CustomDropdownItemComponentProps) => {
  const topic = useSelector(getTmTopic);
  const [isImageDownloading, setIsImageDownloading] = useState(false);
  const [hasImageErrored, setHasImageErrored] = useState(false);
  const track = useTrack();
  const translate = useTranslate();

  useErrorModal({ error: hasImageErrored });

  const exportImage = async () => {
    if (!topic) {
      return;
    }
    track({
      entityId: topic.id,
      entityName: topic.name,
      entityType: "Topic",
      name: "Map Image Export",
      page: "Topic detail",
    });
    try {
      if (!topic.fullsize_map_image_url) {
        throw new Error(`No fullsize_map_image_url for ${topic.id}`);
      }
      setIsImageDownloading(true);
      const { file } = await getImageDownloadFile(topic.fullsize_map_image_url);
      saveAs(file, `${topic.name}.png`);
    } catch {
      setHasImageErrored(true);
    } finally {
      setIsImageDownloading(false);
      handleDeactivate();
    }
  };

  const onClickExport = (event: MouseEvent) => {
    event.preventDefault();
    void exportImage();
  };

  return (
    <DropdownItem index={index} isActive={isActive} isDisabled={isImageDownloading} isProItem onClick={onClickExport}>
      {translate("app.topic.image_export")}
      {isImageDownloading && <StyledLoadingIndicator />}
    </DropdownItem>
  );
};
